import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { isValidEmail } from './composeEmailUtils';

export interface EmailAddressInputProps {
  /** The addresses typed so far, as chips. */
  emails: string[];
  onChange: (emails: string[]) => void;
  /**
   * Told of a typed address that is not one (a message), and of a good one
   * added after it (null) — the host shows errors where it shows its others.
   */
  onError: (message: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Addresses typed by hand, each a removable chip — Enter or "Add" takes what
 * is in the field. The same piece wherever somebody may write to an address
 * that is not on the record: the email composer, a request for files.
 */
export function EmailAddressInput({
  emails,
  onChange,
  onError,
  placeholder = 'Add another email address…',
  disabled = false,
}: EmailAddressInputProps) {
  const [draft, setDraft] = useState('');

  function add() {
    const email = draft.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      onError(`"${email}" is not a valid email address.`);
      return;
    }
    onError(null);
    if (!emails.some((e) => e.toLowerCase() === email.toLowerCase())) {
      onChange([...emails, email]);
    }
    setDraft('');
  }

  return (
    <div>
      {emails.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="manual-recipient-chips">
          {emails.map((email) => (
            <span
              key={email}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full"
            >
              {email}
              <button
                type="button"
                aria-label={`Remove ${email}`}
                disabled={disabled}
                onClick={() => onChange(emails.filter((e) => e !== email))}
                // A finger's 44px on a phone without making the chip that tall.
                className="inline-flex items-center justify-center min-h-11 min-w-11 -my-3 -mr-3 sm:min-h-0 sm:min-w-0 sm:my-0 sm:mr-0 text-gray-400 hover:text-red-600"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2 mt-2">
        <input
          type="email"
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className="flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          data-testid="manual-email-input"
        />
        <button
          type="button"
          onClick={add}
          disabled={disabled}
          className="flex items-center gap-1 min-h-11 sm:min-h-0 px-3 py-2 text-sm text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 disabled:opacity-50"
          data-testid="add-manual-email"
        >
          <Plus size={14} />
          Add
        </button>
      </div>
    </div>
  );
}
